import { useDispatch, useSelector } from "react-redux";
import { createToClient, deleteToClient, fetchToClient, updateToClient, setLastCreatedToClient } from "../../data/slices/toClientSlice";
import { notify } from "../../settings/toastSetting";
import ToClient from "../../data/schemas/toClient";
import { useEffect, useState, useMemo } from "react";
import StyledTable from "../../components/table-component/StyledTable";
import Transform from "../../components/TransForm/Transform";
import { 
    Button, 
    Dialog, 
    Box, 
    Typography, 
    InputBase, 
    Paper,
    IconButton,
    Tooltip,
    Fade
} from "@mui/material";
import { 
    Refresh as RefreshIcon,
    Add as AddIcon,
    Business as BusinessIcon,
    People as PeopleIcon,
    LocationOn as LocationIcon,
    Search as SearchIcon
} from "@mui/icons-material";
import { output, sortByProp } from "../../settings/themeSettings";
import './ToClientPage.scss';

const ToClientPage = (props) => {
    const { toClients } = useSelector((state) => state.toClients);
    const dispatch = useDispatch();

    const [toClient, setToClient] = useState(null);
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const handleClose = () => {
        setOpen(false);
        setToClient(null);
    }

    const handleRefresh = () => {
        dispatch(fetchToClient());
    };

    const addToClient = (data, cb) => {
        handleClose();
        if (data.id) {
            updateToClient(data, (err) => {
                output(err);
            }, (resp) => {
                cb();
                notify("Le TO/Client a été enregistré avec succès !");
                dispatch(fetchToClient());
            });
            return;
        }

        createToClient(data, (err) => {
            output(err);
        }, (resp) => {
            cb();
            notify("Le TO/Client a été ajouté avec succès !");
            dispatch(setLastCreatedToClient(resp)); // Stockage pour préchargement
            dispatch(fetchToClient());
        });
    }

    const onToClientSelected = (id) => {
        setToClient(toClients.find((t) => t.id === id));
        setOpen(true);
    }

    const onDelete = (id) => {
        handleClose();
        deleteToClient(id, (err) => {
            output(err);
        }, (resp) => {
            notify("Le TO/Client a bien été supprimé !");
            dispatch(fetchToClient());
        });
    }

    useEffect(() => {
        dispatch(fetchToClient());
    }, [dispatch]);

    const filteredToClients = useMemo(() => {
        return toClients.filter(item => 
            item.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.referent?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.ville?.toLowerCase().includes(searchTerm.toLowerCase())
        ).sort((a, b) => sortByProp(a, b, 'libelle'));
    }, [toClients, searchTerm]);

    const displayColumns = ["libelle", "code", "referent", "telephone", "courriel", "ville"];

    return (
        <Fade in={true} timeout={800}>
            <div className='to-client-page'>
                <Box className="page-header">
                    <Box className="title-section">
                        <Typography variant="h4" component="h1">Gestion des TO / Clients</Typography>
                        <Typography variant="body1">Gérez vos partenaires Tour Opérateurs et Agences de voyage</Typography>
                    </Box>
                    <Box className="action-section">
                        <Tooltip title="Actualiser la liste">
                            <IconButton onClick={handleRefresh} sx={{ bgcolor: 'white', mr: 1, boxShadow: 1 }}>
                                <RefreshIcon color="primary" />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpen(true)}
                            sx={{
                                borderRadius: '12px',
                                padding: '10px 24px',
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                }
                            }}
                        >
                            Nouveau Partenaire
                        </Button>
                    </Box>
                </Box>

                <div className="stats-container">
                    <div className="stat-card">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div className="label">Total Partenaires</div>
                            <BusinessIcon sx={{ color: '#3b82f6', opacity: 0.8 }} />
                        </Box>
                        <div className="value">{toClients.length}</div>
                        <div className="trend neutral">Base de données active</div>
                    </div>
                    <div className="stat-card">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div className="label">Référents</div>
                            <PeopleIcon sx={{ color: '#8b5cf6', opacity: 0.8 }} />
                        </Box>
                        <div className="value">{toClients.filter(t => t.referent).length}</div>
                        <div className="trend positive">Contacts assignés</div>
                    </div>
                    <div className="stat-card">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div className="label">Villes</div>
                            <LocationIcon sx={{ color: '#f59e0b', opacity: 0.8 }} />
                        </Box>
                        <div className="value">{new Set(toClients.map(t => t.ville)).size}</div>
                        <div className="trend neutral">Implantations</div>
                    </div>
                </div>

                <div className="list-controls">
                    <Paper className="search-box" elevation={0}>
                        <SearchIcon />
                        <InputBase
                            placeholder="Rechercher par nom, code, référent ou ville..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Paper>
                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                        {filteredToClients.length} résultat(s) trouvé(s)
                    </Typography>
                </div>

                <div className="table-wrapper">
                    <StyledTable
                        rows={filteredToClients}
                        head={displayColumns}
                        onOptionClicked={onToClientSelected}
                    />
                </div>

                <Dialog 
                    onClose={handleClose} 
                    open={open}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: '20px', padding: '16px' }
                    }}
                >
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
                            {toClient ? 'Détails du Partenaire' : 'Nouveau TO / Client'}
                        </Typography>
                        <Transform
                            onSubmit={addToClient}
                            mandatories={["libelle", "code"]}
                            entity={new ToClient()}
                            toUpdate={toClient}
                            onDelete={onDelete}
                            onCancel={handleClose}
                        />
                    </Box>
                </Dialog>
            </div>
        </Fade>
    );
}

export default ToClientPage;
